import { Link } from 'react-router-dom';
import { Card } from '@/components/ui';
import { formatPhone } from '@/lib/phoneUtils';
import type { SupplierListItem } from '../types';

interface SupplierCardProps {
  supplier: SupplierListItem;
  onEdit?: () => void;
}

export function SupplierCard({ supplier, onEdit }: SupplierCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow h-full flex flex-col">
      <div className="p-4 flex flex-col h-full">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <Link
              to={`/suppliers/${supplier.id}`}
              className="text-lg font-semibold text-secondary-900 hover:text-primary-600
                         transition-colors block truncate"
            >
              {supplier.name}
            </Link>
          </div>

          {/* Action Buttons */}
          {onEdit && (
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              <button
                type="button"
                onClick={onEdit}
                className="p-1.5 text-secondary-400 hover:text-primary-600
                           hover:bg-primary-50 rounded transition-colors"
                title="Edit supplier"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Contact Info Section */}
        <div className="space-y-2 text-sm flex-1">
          {/* Email */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            {supplier.contactEmail ? (
              <a
                href={`mailto:${supplier.contactEmail}`}
                className="text-primary-600 hover:underline truncate"
              >
                {supplier.contactEmail}
              </a>
            ) : (
              <span className="text-secondary-400 italic">No email</span>
            )}
          </div>

          {/* Phone */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              />
            </svg>
            {supplier.phone ? (
              <span className="text-secondary-600">{formatPhone(supplier.phone)}</span>
            ) : (
              <span className="text-secondary-400 italic">No phone</span>
            )}
          </div>

          {/* Location */}
          <div className="flex items-center gap-2 min-h-[20px]">
            <svg
              className="w-4 h-4 text-secondary-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {supplier.displayLocationText ? (
              <span className="text-secondary-600 truncate">
                {supplier.displayLocationText}
              </span>
            ) : (
              <span className="text-secondary-400 italic">No locations</span>
            )}
          </div>
        </div>

        {/* Footer Stats Section */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-secondary-100">
          <div className="flex items-center gap-1.5 text-sm text-secondary-500">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span>
              {supplier.locationCount}{' '}
              {supplier.locationCount === 1 ? 'location' : 'locations'}
            </span>
          </div>
          {supplier.defaultCurrency && (
            <span className="text-xs text-secondary-400 bg-secondary-100 px-2 py-0.5 rounded">
              {supplier.defaultCurrency}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
